import type { GridType, Point2D } from '@library/types'

/* Смещения для окрестности Мура */
const neighborhoodOffset: Point2D[] = [
  { x: -1, y: -1 }, // 0 левый нижний сосед
  { x: -1, y: 0 }, // 1 левый
  { x: -1, y: 1 }, // 2
  { x: 0, y: 1 }, // 3 верхний
  { x: 1, y: 1 }, // 4 правый верхний сосед
  { x: 1, y: 0 }, // 5 правый
  { x: 1, y: -1 }, // 6
  { x: 0, y: -1 }, // 7 нижний
]

/* Индексы для обозначения направления движения, используется совместно с массивом neighborhoodOffset */
const leftNeighbor = 1
const upNeighbor = 3
const rightNeighbor = 5
const downNeighbor = 7

function getCellCoordKey(cell: Point2D): string {
  return `${cell.x}-${cell.y}`
}

/* Поиск контура методом Moore-Neighbor Tracing
 *  https://www.imageprocessingplace.com/downloads_V3/root_downloads/tutorials/contour_tracing_Abeer_George_Ghuneim/alg.html
 *  https://habr.com/ru/articles/467611/
 *  grid - сетка, одномерный массив
 *  columns, rows - количество столбцов и строк в сетке
 *  результат - одномерный массив объектов {x, y} - координаты ячеек в grid, которые являются контуром
 *  Предполагается, что в grid только один контур
 *
 *  Условно будем считать, что начало находится в левом нижнем углу.
 * */
export function findContour(
  grid: GridType['body'],
  colCount: number,
  rowCount: number,
  nullValue: number,
): Point2D[] {
  function isPixelBlack(pixelCoord: Point2D, offset: Point2D) {
    const cell = { x: pixelCoord.x + offset.x, y: pixelCoord.y + offset.y }
    return (
      cell.x >= 0 &&
      cell.x < colCount &&
      cell.y >= 0 &&
      cell.y < rowCount &&
      grid[cell.y * colCount + cell.x] !== nullValue
    )
  }

  function getFromForOffset(offsetIndex: number): number {
    const from = [
      rightNeighbor,
      downNeighbor,
      downNeighbor,
      leftNeighbor,
      leftNeighbor,
      upNeighbor,
      upNeighbor,
      rightNeighbor,
    ]
    return from[offsetIndex]
  }

  /* Возвращает черный пиксель из окрестности Мура для пикселя
   *
   * @param pixelCoord координаты пикселя, для которого строится окрестность Мура
   * @param comeFrom направление, с которого пришли в пиксель pixelCoord.
   * comeFrom (это индекс в массиве neighborhoodOffset,
   * который указывает на пиксель относительно пикселя pixelCoord)
   * @returns { next, comeFrom } next - координаты найденного черного пикселя, from - направление входа в этот пиксель
   * */
  function getNextPixelOfMoreNeighborhood(pixelCoord: Point2D, comeFrom: number) {
    let index = comeFrom
    let next = { x: -1, y: -1 }
    let comeFromForNext = -1
    do {
      index++
      if (index >= neighborhoodOffset.length) {
        index = 0
      }
      const offset = neighborhoodOffset[index]
      if (isPixelBlack(pixelCoord, offset)) {
        next = { x: pixelCoord.x + offset.x, y: pixelCoord.y + offset.y }
        comeFromForNext = getFromForOffset(index)
        break
      }
    } while (index !== comeFrom)
    return { next, comeFrom: comeFromForNext }
  }

  const contour = []
  const setContour = new Set()

  // ищем первый черный пиксель
  let startPixelCoord: Point2D = { x: -1, y: -1 }
  for (let iRow = 0; iRow < rowCount; iRow++) {
    for (let iCol = 0; iCol < colCount; iCol++) {
      if (grid[iRow * colCount + iCol] !== nullValue) {
        startPixelCoord = { x: iCol, y: iRow }
        break
      }
    }
    if (startPixelCoord.x !== -1) {
      break
    }
  }
  if (startPixelCoord.x === -1) return []

  let startCellVisitedCount = 1
  contour.push({ ...startPixelCoord })
  setContour.add(getCellCoordKey(startPixelCoord))

  let currentPixel: Point2D = { ...startPixelCoord }
  let currentComeFrom = downNeighbor

  while (startCellVisitedCount < 3) {
    const temp = getNextPixelOfMoreNeighborhood(currentPixel, currentComeFrom)
    const nextPixel = temp.next
    currentComeFrom = temp.comeFrom
    if (nextPixel.x === -1 && nextPixel.y === -1) throw new Error('Этой ситуации не должно быть')
    if (nextPixel.x === startPixelCoord.x && nextPixel.y === startPixelCoord.y) {
      startCellVisitedCount++
    }
    if (!setContour.has(getCellCoordKey(nextPixel))) {
      contour.push({ ...nextPixel })
      setContour.add(getCellCoordKey(nextPixel))
    }
    currentPixel = nextPixel
  }

  return contour
}
